import { useMemo, useState } from "react";
import { ChevronRight, Box } from "lucide-react";
import { LayerLegend } from "@/components/viewer/ViewerToolbar";

/** Model tree grouped Elevation → Level → Panel, plus the Rhino layer tree. */
export function ModelTree({ panels, layers, selection, onSelect, hiddenLayers, toggleLayer, layersDisabled = false }) {
  const [expanded, setExpanded] = useState(new Set());

  const tree = useMemo(() => {
    const root = new Map();
    panels.forEach((panel) => {
      const elevation = panel.elevation || "—";
      const level = panel.level || "—";
      if (!root.has(elevation)) root.set(elevation, new Map());
      const levels = root.get(elevation);
      if (!levels.has(level)) levels.set(level, []);
      levels.get(level).push(panel);
    });
    return root;
  }, [panels]);

  const toggle = (key) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpanded(next);
  };

  return (
    <div
      data-testid="model-tree"
      className="flex h-full w-72 max-w-[80vw] flex-col border-r border-slate-200 bg-white max-lg:absolute max-lg:inset-y-0 max-lg:left-0 max-lg:z-30 max-lg:shadow-2xl"
    >
      <div className="border-b border-slate-200 px-4 py-3">
        <p className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Model Tree</p>
      </div>
      <div className="okn-scroll flex-1 overflow-y-auto p-2">
        {[...tree.entries()].map(([elevation, levels]) => {
          const elevationKey = `E:${elevation}`;
          const count = [...levels.values()].reduce((sum, list) => sum + list.length, 0);
          return (
            <div key={elevationKey}>
              <button
                data-testid={`tree-elevation-${elevation}`}
                onClick={() => toggle(elevationKey)}
                className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-xs text-slate-800 hover:bg-slate-100"
              >
                <ChevronRight
                  size={13}
                  className={`transition-transform ${expanded.has(elevationKey) ? "rotate-90" : ""}`}
                />
                Elevation {elevation}
                <span className="mono ml-auto text-[10px] text-slate-500">{count}</span>
              </button>
              {expanded.has(elevationKey)
                ? [...levels.entries()].map(([level, list]) => {
                    const levelKey = `${elevationKey}/L:${level}`;
                    return (
                      <div key={levelKey} className="ml-4">
                        <button
                          data-testid={`tree-level-${elevation}-${level}`}
                          onClick={() => toggle(levelKey)}
                          className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-100"
                        >
                          <ChevronRight
                            size={12}
                            className={`transition-transform ${
                              expanded.has(levelKey) ? "rotate-90" : ""
                            }`}
                          />
                          {level}
                          <span className="mono ml-auto text-[10px] text-slate-500">
                            {list.length}
                          </span>
                        </button>
                        {expanded.has(levelKey)
                          ? list.map((panel) => (
                              <button
                                key={panel.panel_uid}
                                data-testid={`tree-panel-${panel.panel_name}`}
                                onClick={() => onSelect([panel.panel_uid])}
                                className={`mono ml-5 flex w-[calc(100%-1.25rem)] items-center gap-1.5 truncate rounded px-2 py-1 text-left text-[10.5px] hover:bg-slate-100 ${
                                  selection.includes(panel.panel_uid)
                                    ? "bg-cyan-500/15 text-cyan-700"
                                    : "text-slate-600"
                                }`}
                              >
                                <Box size={10} className="shrink-0" />
                                {panel.panel_name}
                              </button>
                            ))
                          : null}
                      </div>
                    );
                  })
                : null}
            </div>
          );
        })}
      </div>
      <LayerLegend
        layers={layers}
        hidden={hiddenLayers}
        toggle={toggleLayer}
        disabled={layersDisabled}
      />
    </div>
  );
}
